import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, SectionTitle, Field, SubmitButton, LoadingState, ErrorState } from "@/components/common";
import { PlanGate } from "@/components/PlanGate";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const NUMS = ["max_reminders", "interval_days", "lead_days", "send_hour", "km_margin"];

function Inner() {
  const { data, loading, error, reload } = useFetch("/followups/settings");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setF({ rules: data.rules, templates: data.templates }); }, [data]);
  if (loading || !f) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const setRule = (k, v) => setF({ ...f, rules: { ...f.rules, [k]: v } });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const rules = { ...f.rules, ...Object.fromEntries(NUMS.map((k) => [k, Number(f.rules[k])])) };
      await api.put("/followups/settings", { rules, templates: f.templates });
      toast.success(t("common.saved"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="reminder-settings-form">
      <Card>
        <SectionTitle hint={t("fu.rulesHint")}>{t("fu.rules")}</SectionTitle>
        <label className="mb-4 flex items-center gap-2 text-sm"><Switch checked={f.rules.reminders_enabled} onCheckedChange={(v) => setRule("reminders_enabled", v)} data-testid="rules-enabled-switch" /> {t("fu.rulesEnabled")}</label>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {NUMS.map((k) => <Field key={k} label={t(`fu.rule.${k}`)} htmlFor={`r-${k}`}><Input id={`r-${k}`} type="number" min={0} required value={f.rules[k]} onChange={(e) => setRule(k, e.target.value)} data-testid={`rule-${k}-input`} /></Field>)}
        </div>
      </Card>
      <Card>
        <SectionTitle hint={t("fu.templatesHint")}>{t("fu.templates")}</SectionTitle>
        <div className="space-y-4">
          {Object.keys(f.templates).map((k) => <Field key={k} label={t(`fu.tpl.${k}`)} htmlFor={`t-${k}`}><Textarea id={`t-${k}`} rows={2} value={f.templates[k]} onChange={(e) => setF({ ...f, templates: { ...f.templates, [k]: e.target.value } })} data-testid={`template-${k}-input`} /></Field>)}
        </div>
      </Card>
      <SubmitButton busy={busy} data-testid="reminder-settings-save">{t("common.save")}</SubmitButton>
    </form>
  );
}

export function ReminderSettings() {
  return <PlanGate feature="follow_ups" managers><Inner /></PlanGate>;
}
